import React, { useState, useEffect } from 'react';
import { User, Badge } from '../../types';
import { api } from '../../utils/api';
import LoadingSpinner from '../ui/LoadingSpinner';
import BadgeCard from '../ui/BadgeCard';

interface BadgesTabProps {
  student: User;
}

const BadgesTab: React.FC<BadgesTabProps> = ({ student }) => {
  const [allBadges, setAllBadges] = useState<Badge[]>([]);
  const [earnedBadges, setEarnedBadges] = useState<Badge[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        setLoading(true);
        setError(null);
        const [allBadgesData, earnedBadgesData] = await Promise.all([
          api.get<Badge[]>('/api/v1/badges'),
          api.get<Badge[]>(`/api/v1/student/${student.user_id}/badges`)
        ]);
        setAllBadges(allBadgesData);
        setEarnedBadges(earnedBadgesData);
      } catch (e) {
        if (e instanceof Error) setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBadges();
  }, [student.user_id]);

  if (loading) return <LoadingSpinner message="Loading badges..." />;
  if (error) return <p className="text-red-500 text-center bg-red-100 p-3 rounded-md">{error}</p>;

  const earnedBadgeNames = new Set(earnedBadges.map(b => b.badge_name));
  const unearnedBadges = allBadges.filter(b => !earnedBadgeNames.has(b.badge_name));

  return (
    <div className="space-y-12">
      <div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4 border-b pb-2">Earned Badges</h2>
        {earnedBadges.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {earnedBadges.map(badge => (
              <BadgeCard key={badge.badge_name} badge={badge} isEarned={true} />
            ))}
          </div>
        ) : (
          <p className="text-gray-600">You have not earned any badges yet. Keep practicing!</p>
        )}
      </div>
      <div>
        <h2 className="text-3xl font-bold text-gray-800 mb-4 border-b pb-2">Un-earned Badges</h2>
        {unearnedBadges.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {unearnedBadges.map(badge => (
              <BadgeCard key={badge.badge_name} badge={badge} isEarned={false} />
            ))}
          </div>
        ) : (
           <p className="text-green-600 font-semibold">Congratulations! You've earned all available badges!</p>
        )}
      </div>
    </div>
  );
};

export default BadgesTab;
